export type LandingAudienceId = 'building' | 'manager' | 'resident';

export type LandingFeatureIconId =
  | 'ledger'
  | 'reconciliation'
  | 'security'
  | 'maintenance'
  | 'documents'
  | 'vehicles'
  | 'amenities'
  | 'community'
  | 'resident-portal';

export interface LandingFeature {
  icon: LandingFeatureIconId;
  headline: string;
  body: string;
  points: string[];
}

export interface LandingFeaturesLead {
  headline: string;
  body: string;
  // The four allocation methods, named so the lead reads as the mechanism
  // summary while the hero's AllocationCard carries the worked proof.
  methods: string[];
}


export interface LandingAllocationRow {
  unit: string;
  share: string;
  amount: string;
}

export interface LandingContent {
  meta: { title: string; description: string };
  nav: { product: string; workflow: string; pricing: string; faq: string; features: string; guide: string; calculator: string; login: string; cta: string };
  ui: {
    monthly: string;
    annual: string;
    annualSaving: string;
    billingFrequency: string;
    monthSuffix: string;
    yearSuffix: string;
    perUnitMonthSuffix: string;
    trial: string;
    billedMonthly: string;
  };
  hero: {
    eyebrow: string;
    title: string;
    body: string;
    primaryCta: string;
    secondaryCta: string;
    note: string;
    proof: string[];
    allocation: {
      label: string;
      title: string;
      expense: string;
      method: string;
      unitColumn: string;
      shareColumn: string;
      amountColumn: string;
      expenseColumn: string;
      methodColumn: string;
      totalLabel: string;
      totalAmount: string;
      rows: LandingAllocationRow[];
      total: string;
      resolved: string;
      footnote: string;
    };
  };
  audience: {
    eyebrow: string;
    title: string;
    body: string;
    items: Array<{ id: LandingAudienceId; label: string; title: string; body: string; points: string[]; cta: string }>;
  };
  monthlyClose: {
    eyebrow: string;
    title: string;
    body: string;
    steps: Array<{ title: string; body: string }>;
  };
  features: {
    eyebrow: string;
    title: string;
    body: string;
    lead: LandingFeaturesLead;
    items: LandingFeature[];
  };
  residents: {
    eyebrow: string;
    title: string;
    body: string;
    points: string[];
    joinLabel: string;
    joinNote: string;
    dualIdentity: { title: string; body: string };
  };
  precision: {
    eyebrow: string;
    title: string;
    body: string;
    facts: Array<{ title: string; body: string }>;
    calculator: { title: string; body: string; cta: string };
  };
  trust: {
    eyebrow: string;
    title: string;
    facts: Array<{ title: string; body: string }>;
  };
  pricing: {
    eyebrow: string;
    title: string;
    body: string;
    chooser: string;
    basicChoice: string;
    proChoice: string;
    basic: { name: string; audience: string; propertyScope: string; features: string[]; cta: string };
    pro: { name: string; audience: string; propertyScope: string; minimum: string; features: string[]; cta: string };
    footnote: string;
  };
  faq: { eyebrow: string; title: string; items: Array<{ question: string; answer: string }> };
  demo: {
    eyebrow: string;
    title: string;
    body: string;
    readOnly: string;
    readOnlyNote: string;
    email: string;
    password: string;
    cta: string;
    footnote: string;
  };
  finalCta: {
    eyebrow: string;
    title: string;
    body: string;
    managerCta: string;
    managerNote: string;
    residentCta: string;
  };
  footer: { tagline: string; product: string; account: string; copyright: string };
}

const en: LandingContent = {
  meta: {
    title: 'Koinon - Property and Community Management',
    description: 'Streamline building finances, calculate common expenses with mathematical precision, and provide every resident with a dedicated portal.',
  },
  nav: { product: 'Product', workflow: 'How it works', pricing: 'Pricing', faq: 'FAQ', features: 'Features', guide: 'Documentation', calculator: 'Expense calculator', login: 'Sign in', cta: 'Create account' },
  ui: {
    monthly: 'Monthly', annual: 'Annual', annualSaving: 'Save 17%', billingFrequency: 'Basic billing frequency',
    monthSuffix: '/ month', yearSuffix: '/ year', perUnitMonthSuffix: '/ unit / month',
    trial: '30-day trial', billedMonthly: 'Billed monthly',
  },
  hero: {
    eyebrow: 'Property and community management',
    title: 'Common expenses, building finances, and resident management in one place.',
    body: 'Koinon streamlines building administration for committees, property managers, and residents. Calculate common expenses, issue monthly statements, track payments, and manage notices, maintenance, and votes.',
    primaryCta: 'Create account',
    secondaryCta: 'How the monthly cycle works',
    note: 'No credit card is required to start.',
    proof: ['English and Greek', 'No credit card to start', 'Cancel any time'],
    allocation: {
      label: 'Example',
      title: 'Lift maintenance, February',
      expense: '€1,000.00 common expense',
      method: 'Surface area (m²)',
      unitColumn: 'Unit', shareColumn: 'Share', amountColumn: 'Amount',
      expenseColumn: 'Common expense', methodColumn: 'Method',
      totalLabel: 'Total allocated', totalAmount: '€1,000.00',
      rows: [
        { unit: 'Unit 1', share: '75 m²', amount: '€294.12' },
        { unit: 'Unit 2', share: '85 m²', amount: '€333.33' },
        { unit: 'Unit 3', share: '95 m²', amount: '€372.55' },
      ],
      total: '3 units · 255 m²',
      resolved: 'The three shares add up to €1,000.00',
      footnote: 'Koinon calculates the exact share for each flat, using equal distribution, surface area, number of bedrooms, or the building’s own millesimal weights.',
    },
  },
  audience: {
    eyebrow: 'Built for communities',
    title: 'Tailored for every role in the building',
    body: 'Committees, property managers, and residents work from a single source of truth, with role-based access ensuring everyone sees what matters to them.',
    items: [
      {
        id: 'building', label: 'Self-managed building', title: 'Building committees',
        body: 'For buildings where owners or an elected committee look after the property themselves.',
        points: ['One property and up to 20 flats', 'Common expenses and monthly statements', 'Notices, votes, and resident portal'],
        cta: 'Create account',
      },
      {
        id: 'manager', label: 'Management company', title: 'Property managers',
        body: 'For professional teams that manage multiple properties with dedicated staff for each.',
        points: ['Unlimited properties and units', 'Staff access by role and building', 'Portfolio-wide financial and operational records'],
        cta: 'Create account',
      },
      {
        id: 'resident', label: 'Resident', title: 'Residents and owners',
        body: 'For tenants and unit owners. Check current balances, submit maintenance requests, and participate in community decisions.',
        points: ['Account balances and statements', 'Maintenance requests and parking', 'Notices, voting, and amenities'],
        cta: 'Sign in to portal',
      },
    ],
  },
  monthlyClose: {
    eyebrow: 'Workflow',
    title: 'The monthly billing and expense cycle',
    body: 'A structured five-step workflow each month, whether managed by a volunteer committee or a professional property management team.',
    steps: [
      { title: 'Record expense', body: 'Enter each common cost once, attach the digital invoice, and assign the relevant billing period.' },
      { title: 'Select allocation method', body: 'Distribute costs equally, by surface area (m²), bedroom count, or the property’s registered millesimal weights.' },
      { title: 'Calculate unit shares', body: 'Koinon calculates the exact financial share for every unit with cent-level mathematical precision.' },
      { title: 'Issue monthly statements', body: 'Combine recurring and one-off expenses into comprehensive monthly balance sheets and individual unit statements.' },
      { title: 'Reconcile bank payments', body: 'Import your bank statement to automatically match incoming payments against outstanding balances.' },
    ],
  },
  precision: {
    eyebrow: 'Integrity',
    title: 'Deterministic accounting and financial integrity',
    body: 'Communal property accounting requires strict accuracy. Koinon eliminates manual ledger errors through mathematical consistency.',
    facts: [
      { title: 'A credit stays a credit', body: 'Overpayments remain credited to the specific unit. Credit is never offset against another unit’s debt or buried in general balances.' },
      { title: 'Balances are computed from the ledger', body: 'Every balance is derived directly from recorded charges and payments, preventing phantom numbers or out-of-sync totals.' },
      { title: 'Zero-discrepancy allocation', body: 'All four allocation methods calculate penny-perfect unit shares so the sum of individual charges always matches the invoice total exactly.' },
      { title: 'Idempotent bank statement imports', body: 'Re-importing a bank statement detects previously recorded transactions, ensuring duplicate entries are never created.' },
    ],
    calculator: {
      title: 'Try the allocation on your own building',
      body: 'Enter an expense and your unit weights. The calculator uses the same allocation engine as the Koinon ledger, so the distributed total always matches the expense exactly.',
      cta: 'Open the calculator',
    },
  },
  features: {
    eyebrow: 'Platform',
    title: 'Every part of running a building, in one place',
    body: 'The ledger, the operations, and the resident portal share one source of truth. Turn a module on per building and it appears for exactly the people who need it.',
    lead: {
      headline: 'Costs that add up, to the cent',
      body: "Pick the method that fits each expense. Each unit's share is calculated to the cent, and the shares always sum back to the total.",
      methods: ['Equal split', 'Surface area (m²)', 'Bedrooms', 'Millesimal weights'],
    },
    items: [
      {
        icon: 'ledger', headline: 'One ledger, every balance traceable',
        body: "Charges, payments, recurring drafts, and adjustments sit in one ledger. Each unit's balance is computed from its own records, and statements print from the same numbers.",
        points: ['Charges, payments, and recurring drafts', 'Per-unit balances and debtor view', 'Printable statements and receipts'],
      },
      {
        icon: 'reconciliation', headline: 'Bank statements matched, not retyped',
        body: 'Import a CSV, camt.053 SEPA XML, or OFX statement. Koinon proposes a match for each payment against the amounts owed, and posts only what you approve. Re-importing the same file recognises what is already recorded, so nothing is entered twice.',
        points: ['CSV, camt.053 SEPA XML, and OFX import', 'Suggested matches you approve', 'No duplicate entries on re-import'],
      },
      {
        icon: 'security', headline: 'Role-based access, with a trail',
        body: 'Managers, staff, and residents reach only the data their role allows. Accounts can be protected with a passkey or an authenticator app, changes are kept in an audit history, and the workspace can export an encrypted backup.',
        points: ['Access scoped by role and workspace', 'Passkeys (WebAuthn) and TOTP 2FA', 'Audit history and encrypted backup export'],
      },
      {
        icon: 'maintenance', headline: 'Faults tracked from report to repair',
        body: "A fault becomes a work order with a status, evidence, and an assigned contractor. The cost of the repair lands back in the building's ledger.",
        points: ['Maintenance requests and work orders', 'Contractor assignment', 'Repair costs posted to the ledger'],
      },
      {
        icon: 'documents', headline: 'Bylaws, minutes, and certificates, kept current',
        body: "Store the building's records in one place with versions, an expiry date where one applies, and who can see each file.",
        points: ['Versioned document storage', 'Expiry dates for certificates', 'Per-file visibility'],
      },
      {
        icon: 'vehicles', headline: 'Vehicles and parking bays, assigned',
        body: "Register each resident's vehicles and assign the parking bays that come with the unit, so the register reflects who actually parks where.",
        points: ['Vehicle registry per unit', 'Parking bay assignment', 'Resident-visible register'],
      },
      {
        icon: 'amenities', headline: 'Shared spaces booked, not argued over',
        body: 'Residents reserve a shared amenity for a time slot. Bookings and the amenity’s own rules are managed per building, and only where the module is on.',
        points: ['Resident self-service booking', 'Per-amenity rules and configuration', 'Module-gated per building'],
      },
      {
        icon: 'community', headline: 'Announcements that show who read them',
        body: "Publish a notice to the building and see who has opened it. Run a poll weighted by millesimal share or one vote per unit, with the result recorded for the assembly.",
        points: ['Notices with read receipts', 'Weighted or per-unit polls', 'Results kept for the record'],
      },
      {
        icon: 'resident-portal', headline: 'Every resident sees their own position',
        body: 'A resident opens their portal and finds their balance, statement, requests, and bookings for the units they hold, reached by an emailed invitation. No asking the manager for a number.',
        points: ['Per-unit balance and statement', 'Requests, bookings, and vehicles', 'Invitation-based access, scoped to their units'],
      },
    ],
  },
  residents: {
    eyebrow: 'Resident Portal',
    title: 'A transparent portal for every resident',
    body: 'Provide residents with direct access to their unit details and the building’s financial status through an emailed invitation. Residents see what they owe and exactly where communal funds are spent.',
    points: [
      'Clear breakdown of current balances and individual charges',
      'Full visibility into building expenses and reserve fund status',
      'Submit maintenance requests and track repair progress',
      'Reserve shared amenities, register vehicles, and confirm parking bays',
    ],
    joinLabel: 'Sign in to portal',
    joinNote: 'Residents access the portal after receiving an email invitation from their building administration or unit owner.',
    dualIdentity: {
      title: 'One account for both manager and resident roles',
      body: 'Committee members who also own or rent a unit switch seamlessly between administration and resident views without separate logins.',
    },
  },
  trust: {
    eyebrow: 'Security',
    title: 'Role-based permissions and data protection',
    facts: [
      { title: 'Granular role-based access', body: 'Property managers, auditors, and residents each access only the data and tools authorized for their role.' },
      { title: 'Passkeys and two-factor authentication', body: 'Protect accounts with hardware passkeys, authenticator apps, and secure recovery codes.' },
      { title: 'Isolated workspace architecture', body: 'Property, financial, and resident data is strictly scoped to its authorized workspace and tenant boundaries.' },
      { title: 'Encrypted backups and audit trails', body: 'Workspace administrators can export encrypted backups and monitor comprehensive activity audit logs.' },
    ],
  },
  pricing: {
    eyebrow: 'Pricing',
    title: 'Transparent plans for buildings of any size',
    body: 'Simple, transparent pricing with no hidden fees. Both plans include a full 30-day free trial, with clear billing terms before any charge.',
    chooser: 'Choose the plan that fits your management model',
    basicChoice: 'Single building, managed by its committee',
    proChoice: 'Multiple properties, managed by a team',
    basic: {
      name: 'Basic', audience: 'Self-managed building', propertyScope: 'One property, up to 20 units',
      features: ['Common expense calculation and monthly charges', 'Equal, area, bedroom, and millesimal allocation', 'Statements, ledger balances, and printable receipts', 'Maintenance requests, contractors, and documents', 'Resident portal with notices, polls, and amenities', 'Vehicle registry and parking bay assignments', 'Bank statement reconciliation and recurring charges', 'Interface available in English and Greek'],
      cta: 'Create account',
    },
    pro: {
      name: 'Portfolio Pro', audience: 'Property management companies', propertyScope: 'Unlimited properties',
      minimum: '€50 / month minimum',
      features: ['Everything in Basic across unlimited properties', 'Instant switching between properties', 'Multi-user staff access with role-based permissions', 'Portfolio-wide financial and operational analytics', 'Priority support for larger portfolios'],
      cta: 'Create account',
    },
    footnote: 'Both plans begin with a 30-day trial. Billing details are confirmed before any subscription is activated.',
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Frequently asked questions',
    items: [
      { question: 'How are common costs split?', answer: 'You select the appropriate allocation method for each expense: equal distribution, surface area (m²), bedroom count, or the property’s registered millesimal shares. Koinon calculates each unit’s exact share down to the cent, guaranteeing that the sum of all charges matches the original cost exactly.' },
      { question: 'What happens if someone overpays?', answer: 'Overpayments are automatically credited to the specific unit’s ledger. A credit on one flat is never applied to cover another unit’s arrears, preserving clear, auditable records for every account.' },
      { question: 'How long does the monthly billing cycle take?', answer: 'Recurring expenses are entered once and automatically carry forward. Koinon calculates distributions, statement totals, and unit balances instantly, making your monthly close a quick review rather than hours of manual calculation.' },
      { question: 'Who can use Koinon?', answer: 'Self-managed building committees, professional property management firms, and individual residents. A single user account can hold managerial permissions while linked as a resident to a unit, switching views with a single click.' },
      { question: 'Can residents get their own access?', answer: 'Yes. Residents access their portal after receiving an email invitation from their building manager or unit owner to view their current balance, detailed statement breakdown, payment receipts, maintenance requests, announcements, and reserve fund status.' },
      { question: 'Does Koinon support bank statements?', answer: 'Yes. Koinon imports CSV and OFX bank statements, automatically suggests matches for incoming payments against outstanding balances, and only commits entries you approve. Re-importing the same file recognizes existing transactions to prevent duplicates.' },
      { question: 'Can unused features be disabled?', answer: 'Yes. Maintenance requests, bank reconciliation, document storage, amenities, voting, and vehicle management can be toggled on or off per property, keeping your interface clean and focused.' },
      { question: 'What happens when the trial ends?', answer: 'An active subscription is required to continue making updates. Your plan choices and billing details are presented clearly before any payment is activated.' },
    ],
  },
  demo: {
    eyebrow: 'Live showcase',
    title: 'Explore a real portfolio before you sign up',
    body: 'Browse {{workspaces}} fully populated workspaces covering {{properties}} properties and {{units}} units, with live ledgers, maintenance, notices, polls and documents.',
    readOnly: 'Read-only',
    readOnlyNote: 'This showcase is read-only. You can browse everything, but nothing you do can change the data.',
    email: 'Email',
    password: 'Password',
    cta: 'Sign in to the showcase',
    footnote: 'No registration needed. The account covers every workspace listed above.',
  },
  finalCta: {
    eyebrow: 'Get Started',
    title: 'Bring clarity to your building management',
    body: 'Create your workspace, add your building and units, and experience your first monthly billing cycle with a 30-day free trial.',
    managerCta: 'Create account',
    managerNote: 'For committees and property management teams.',
    residentCta: 'Sign in to resident portal',
  },
  footer: { tagline: 'Modern property and community management for buildings, managers, and residents.', product: 'Product', account: 'Account', copyright: '© 2026 Koinon. All rights reserved.' },
};

const el: LandingContent = {
  meta: { title: 'Koinon - Διαχείριση Ακινήτων και Κοινότητας', description: 'Ολοκληρώστε τον μηνιαίο κύκλο, κατανείμετε κοινόχρηστες δαπάνες ισομερώς, βάσει εμβαδού, υπνοδωματίων ή χιλιοστών, και προσφέρετε σε κάθε κάτοικο πρόσβαση στη δική του καρτέλα.' },
  nav: { product: 'Προϊόν', workflow: 'Πώς λειτουργεί', pricing: 'Τιμές', faq: 'Συχνές ερωτήσεις', features: 'Χαρακτηριστικά', guide: 'Οδηγός χρήσης', calculator: 'Υπολογιστής κοινόχρηστων', login: 'Σύνδεση', cta: 'Εγγραφή' },
  ui: {
    monthly: 'Μηνιαία', annual: 'Ετήσια', annualSaving: 'Έκπτωση 17%', billingFrequency: 'Συχνότητα χρέωσης Basic',
    monthSuffix: '/ μήνα', yearSuffix: '/ έτος', perUnitMonthSuffix: '/ διαμέρισμα / μήνα',
    trial: 'Δοκιμή 30 ημερών', billedMonthly: 'Μηνιαία χρέωση',
  },
  hero: {
    eyebrow: 'Διαχείριση ακινήτων και κοινότητας',
    title: 'Έκδοση κοινοχρήστων, οικονομική διαχείριση και οργάνωση πολυκατοικίας.',
    body: 'Το Koinon απλοποιεί τη διαχείριση κτιρίων για διαχειριστικές επιτροπές, εταιρείες διαχείρισης και ενοίκους. Υπολογίζει τα κοινόχρηστα, εκδίδει ειδοποιητήρια, καταγράφει εισπράξεις και οργανώνει ανακοινώσεις, βλάβες και ψηφοφορίες.',
    primaryCta: 'Δημιουργία λογαριασμού',
    secondaryCta: 'Πώς λειτουργεί ο μηνιαίος κύκλος',
    note: 'Δεν απαιτείται πιστωτική κάρτα για την έναρξη.',
    proof: ['Ελληνικά και Αγγλικά', 'Χωρίς πιστωτική κάρτα', 'Ακύρωση οποιαδήποτε στιγμή'],
    allocation: {
      label: 'Παράδειγμα',
      title: 'Συντήρηση ανελκυστήρα, Φεβρουάριος',
      expense: 'Κοινόχρηστη δαπάνη €1.000,00',
      method: 'Εμβαδική αναλογία (m²)',
      unitColumn: 'Διαμέρισμα', shareColumn: 'Εμβαδόν', amountColumn: 'Ποσό',
      expenseColumn: 'Δαπάνη', methodColumn: 'Μέθοδος',
      totalLabel: 'Συνολική κατανομή', totalAmount: '€1.000,00',
      rows: [
        { unit: 'Διαμέρισμα 1', share: '75 m²', amount: '€294,12' },
        { unit: 'Διαμέρισμα 2', share: '85 m²', amount: '€333,33' },
        { unit: 'Διαμέρισμα 3', share: '95 m²', amount: '€372,55' },
      ],
      total: '3 διαμερίσματα · 255 m²',
      resolved: 'Το άθροισμα των μεριδίων ισούται ακριβώς με €1.000,00',
      footnote: 'Το Koinon υπολογίζει το ακριβές ποσό ανά διαμέρισμα, είτε ισομερώς, είτε βάσει τετραγωνικών μέτρων, αριθμού υπνοδωματίων ή των χιλιοστών της πολυκατοικίας.',
    },
  },
  audience: {
    eyebrow: 'Για καθε ρολο',
    title: 'Σχεδιασμένο για όλη την κοινότητα του κτιρίου',
    body: 'Επιτροπές, εταιρείες διαχείρισης και ένοικοι συνδέονται σε ένα κοινό σύστημα, όπου κάθε ρόλος έχει πρόσβαση στις πληροφορίες που τον αφορούν.',
    items: [
      {
        id: 'building', label: 'Αυτοδιαχειριζόμενη πολυκατοικία', title: 'Διαχειριστικές επιτροπές',
        body: 'Για κτίρια που διαχειρίζονται οι ίδιοι οι συνιδιοκτήτες ή μια εκλεγμένη επιτροπή.',
        points: ['Ένα ακίνητο έως 20 διαμερίσματα', 'Κοινόχρηστα και μηνιαία ειδοποιητήρια', 'Ανακοινώσεις, ψηφοφορίες και πρόσβαση ενοίκων'],
        cta: 'Δημιουργία λογαριασμού',
      },
      {
        id: 'manager', label: 'Εταιρεία διαχείρισης', title: 'Επαγγελματίες διαχειριστές',
        body: 'Για επιχειρήσεις που διαχειρίζονται πολλαπλά ακίνητα με διαφορετικούς συνεργάτες ανά κτίριο.',
        points: ['Απεριόριστα ακίνητα και διαμερίσματα', 'Διαβαθμισμένη πρόσβαση προσωπικού ανά ρόλο', 'Πλήρες οικονομικό και λειτουργικό ιστορικό χαρτοφυλακίου'],
        cta: 'Δημιουργία λογαριασμού',
      },
      {
        id: 'resident', label: 'Ένοικος & Ιδιοκτήτης', title: 'Κάτοικοι και συνιδιοκτήτες',
        body: 'Για όσους ζουν στο κτίριο. Άμεση προβολή οφειλών, καταβολών, ανακοινώσεων και αιτημάτων επισκευής.',
        points: ['Υπόλοιπα και ειδοποιητήρια', 'Αιτήματα συντήρησης και θέσεις στάθμευσης', 'Ανακοινώσεις, ψηφοφορίες και κοινόχρηστοι χώροι'],
        cta: 'Σύνδεση στην πύλη',
      },
    ],
  },
  monthlyClose: {
    eyebrow: 'Διαδικασια', title: 'Η διαδικασία του μηνιαίου κύκλου κοινοχρήστων',
    body: 'Πέντε ξεκάθαρα βήματα κάθε μήνα, είτε η διαχείριση γίνεται από την επιτροπή ενοίκων είτε από επαγγελματική εταιρεία διαχείρισης.',
    steps: [
      { title: 'Καταχώριση δαπανών', body: 'Καταχωρίστε το τιμολόγιο ή τη δαπάνη μία φορά, επισυνάπτοντας το παραστατικό και την περίοδο χρέωσης.' },
      { title: 'Επιλογή μεθόδου κατανομής', body: 'Ισομερής κατανομή, βάσει τετραγωνικών, υπνοδωματίων ή των χιλιοστών της πολυκατοικίας ανά κατηγορία δαπάνης.' },
      { title: 'Αυτόματος υπολογισμός μεριδίων', body: 'Το Koinon υπολογίζει το ποσό συμμετοχής κάθε διαμερίσματος με απόλυτη μαθηματική ακρίβεια.' },
      { title: 'Έκδοση ειδοποιητηρίων', body: 'Συγκεντρωτική κατάσταση κοινοχρήστων και ατομικά ειδοποιητήρια με αναλυτικό επιμερισμό.' },
      { title: 'Τραπεζική συμφωνία', body: 'Εισαγωγή extrait (τραπεζικής κίνησης) και αυτόματη αντιστοίχιση πληρωμών με τις αντίστοιχες οφειλές.' },
    ],
  },
  precision: {
    eyebrow: 'Αξιοπιστια', title: 'Απόλυτη λογιστική ακρίβεια και διαφάνεια',
    body: 'Η διαχείριση κοινοχρήστων απαιτεί αξιοπιστία χωρίς σφάλματα στρογγυλοποίησης. Το Koinon εγγυάται μαθηματικά ισοσκελισμένους λογαριασμούς.',
    facts: [
      { title: 'Το πιστωτικό παραμένει πιστωτικό', body: 'Αν ένας ένοικος έχει προκαταβάλει ή καταβάλει επιπλέον χρήματα, το ποσό παραμένει πιστωτικό στη δική του καρτέλα και δεν αναμειγνύεται με οφειλές άλλων.' },
      { title: 'Τα υπόλοιπα υπολογίζονται από το καθολικό', body: 'Κάθε υπόλοιπο προκύπτει δυναμικά από το σύνολο των εγγεγραμμένων χρεώσεων και πληρωμών, αποτρέποντας ασυμφωνίες και αυθαίρετα ποσά.' },
      { title: 'Μηδενικές αποκλίσεις στρογγυλοποίησης', body: 'Και οι τέσσερις μέθοδοι υπολογίζονται με ακρίβεια δεκαδικού, ώστε το άθροισμα των χρεώσεων να ισούται πάντα ακριβώς με τη συνολική δαπάνη.' },
      { title: 'Ασφαλής επανεισαγωγή τραπεζικών αρχείων', body: 'Κατά την εκ νέου εισαγωγή αρχείου extrait, το σύστημα αναγνωρίζει τις ήδη καταχωρισμένες κινήσεις, αποτρέποντας διπλότυπες εγγραφές.' },
    ],
    calculator: {
      title: 'Δοκιμάστε την κατανομή στο δικό σας κτίριο',
      body: 'Συμπληρώστε ένα έξοδο και τα βάρη των διαμερίσμάτων σας. Ο υπολογιστής χρησιμοποιεί την ίδια μηχανή κατανομής με το οικονομικό αρχείο του Koinon, οπότε το σύνολο ταιριάζει πάντα ακριβώς με το έξοδο.',
      cta: 'Άνοιξτε τον υπολογιστή',
    },
  },
  features: {
    eyebrow: 'Πλατφόρμα', title: 'Όλα όσα αφορούν τη διαχείριση ενός κτιρίου, σε ένα μέρος',
    body: 'Το καθολικό, οι λειτουργίες και η πύλη κατοίκων μοιράζονται μία πηγή αλήθειας. Ενεργοποιείτε κάθε λειτουργία ανά κτίριο και εμφανίζεται ακριβώς σε όποιον τη χρειάζεται.',
    lead: {
      headline: 'Έξοδα που αθροίζονται, μέχρι το τελευταίο λεπτό',
      body: 'Επιλέγετε τη μέθοδο που ταιριάζει σε κάθε έξοδο. Το μερίδιο κάθε διαμερίσματος υπολογίζεται μέχρι το λεπτό και τα μερίδια αθροίζουν πάντα ακριβώς στο σύνολο.',
      methods: ['Ίση κατανομή', 'Εμβαδόν (m²)', 'Υπνοδωμάτια', 'Χιλιοστά'],
    },
    items: [
      {
        icon: 'ledger', headline: 'Ένα καθολικό, με κάθε υπόλοιπο εξακριβώσιμο',
        body: 'Χρεώσεις, πληρωμές, επαναλαμβανόμενες πρόχειρες εγγραφές και διορθώσεις σε ένα καθολικό. Το υπόλοιπο κάθε διαμερίσματος προκύπτει από τις δικές του εγγραφές και οι καταστάσεις εκτυπώνονται από τους ίδιους αριθμούς.',
        points: ['Χρεώσεις, πληρωμές και επαναλαμβανόμενες εγγραφές', 'Υπόλοιπα ανά διαμέρισμα και οφειλέτες', 'Εκτυπώσιμες καταστάσεις και αποδείξεις'],
      },
      {
        icon: 'reconciliation', headline: 'Αντιστοίχιση τραπεζικών κινήσεων, χωρίς ξαναπληκτρολόγηση',
        body: 'Εισάγετε κατάσταση σε CSV, camt.053 SEPA XML ή OFX. Το Koinon προτείνει αντιστοίχιση κάθε πληρωμής με τα οφειλόμενα ποσά και καταχωρεί μόνο ό,τι εγκρίνετε. Η εκ νέου εισαγωγή του ίδιου αρχείου αναγνωρίζει ό,τι έχει ήδη καταχωρηθεί, ώστε να μην διπλοκαταχωριστεί τίποτα.',
        points: ['Εισαγωγή CSV, camt.053 SEPA XML και OFX', 'Προτεινόμενες αντιστοιχίες προς έγκριση', 'Χωρίς διπλές εγγραφές σε εκ νέου εισαγωγή'],
      },
      {
        icon: 'security', headline: 'Πρόσβαση ανά ρόλο, με πλήρες ίχνος',
        body: 'Διαχειριστές, προσωπικό και κάτοικοι βλέπουν μόνο όσα επιτρέπει ο ρόλος τους. Οι λογαριασμοί προστατεύονται με passkey ή εφαρμογή αυθεντικοποίησης, οι αλλαγές διατηρούνται στο ιστορικό και ο χώρος εργασίας μπορεί να εξάγει κρυπτογραφημένο αντίγραφο ασφαλείας.',
        points: ['Πρόσβαση ανά ρόλο και χώρο εργασίας', 'Passkeys (WebAuthn) και TOTP 2FA', 'Ιστορικό αλλαγών και κρυπτογραφημένη εξαγωγή'],
      },
      {
        icon: 'maintenance', headline: 'Βλάβες σε παρακολούθηση, από την αναφορά στην επισκευή',
        body: 'Μια βλάβη γίνεται εντολή εργασίας με κατάσταση, στοιχεία και ανατεθειμένο συνεργείο. Το κόστος της επισκευής καταγράφεται στο καθολικό του κτιρίου.',
        points: ['Αιτήματα βλάβης και εντολές εργασίας', 'Ανάθεση σε συνεργείο', 'Κόστος επισκευής στο καθολικό'],
      },
      {
        icon: 'documents', headline: 'Καταστατικό, πρακτικά και πιστοποιητικά, πάντα ενημερωμένα',
        body: 'Φυλάτε τα αρχεία του κτιρίου σε ένα μέρος, με εκδόσεις, ημερομηνία λήξης όπου απαιτείται και ποιος βλέπει κάθε αρχείο.',
        points: ['Αποθήκευση εγγράφων με εκδόσεις', 'Ημερομηνίες λήξης για πιστοποιητικά', 'Ορατότητα ανά αρχείο'],
      },
      {
        icon: 'vehicles', headline: 'Οχήματα και θέσεις στάθμευσης, κατανεμημένες',
        body: 'Καταγράφετε τα οχήματα κάθε κατοίκου και αναθέτετε τις θέσεις στάθμευσης του διαμερίσματος, ώστε το μητρώο να δείχνει ποιος σταθμεύει πού.',
        points: ['Μητρώο οχημάτων ανά διαμέρισμα', 'Ανάθεση θέσεων στάθμευσης', 'Μητρώο ορατό στους κατοίκους'],
      },
      {
        icon: 'amenities', headline: 'Κοινόχρηστοι χώροι με κρατήσεις, χωρίς διενέξεις',
        body: 'Οι κάτοικοι κρατούν έναν κοινόχρηστο χώρο για συγκεκριμένο διάστημα. Οι κρατήσεις και οι κανόνες κάθε χώρου διαχειρίζονται ανά κτίριο και μόνο όπου η λειτουργία είναι ενεργή.',
        points: ['Κρατήσεις από τους ίδιους τους κατοίκους', 'Κανόνες και ρύθμιση ανά χώρο', 'Λειτουργία ανά κτίριο'],
      },
      {
        icon: 'community', headline: 'Ανακοινώσεις που δείχνουν ποιος τις διάβασε',
        body: 'Δημοσιεύετε ανακοίνωση στο κτίριο και βλέπετε ποιος την άνοιξε. Διεξάγετε ψηφοφορία σταθμισμένη κατά χιλιοστά ή μία ψήφος ανά διαμέρισμα, με το αποτέλεσμα καταγεγραμμένο για τη συνέλευση.',
        points: ['Ανακοινώσεις με απόδειξη ανάγνωσης', 'Ψηφοφορίες κατά χιλιοστά ή ανά διαμέρισμα', 'Αποτελέσματα στο αρχείο'],
      },
      {
        icon: 'resident-portal', headline: 'Κάθε κάτοικος βλέπει τη δική του θέση',
        body: 'Ο κάτοικος ανοίγει την πύλη του και βρίσκει υπόλοιπο, κατάσταση, αιτήματα και κρατήσεις για τα διαμερίσματα που κατέχει, μέσω πρόσκλησης με email. Χωρίς να ρωτά τη διαχείριση για έναν αριθμό.',
        points: ['Υπόλοιπο και κατάσταση ανά διαμέρισμα', 'Αιτήματα, κρατήσεις και οχήματα', 'Πρόσβαση με πρόσκληση, στα δικά του διαμερίσματα'],
      },
    ],
  },
  residents: {
    eyebrow: 'Πυλη Ενοικων',
    title: 'Πλήρης διαφάνεια και ηλεκτρονική πρόσβαση για τους ενοίκους',
    body: 'Δώστε στους ενοίκους άμεση πρόσβαση στην καρτέλα του διαμερίσματός τους και στα οικονομικά του κτιρίου μέσω πρόσκλησης email. Οι ένοικοι βλέπουν ακριβώς τι οφείλουν και πού δαπανώνται τα χρήματα της πολυκατοικίας.',
    points: [
      'Αναλυτική εικόνα οφειλών και αιτιολογία κάθε χρέωσης',
      'Πλήρης διαφάνεια στα έξοδα της πολυκατοικίας και στο αποθεματικό ταμείο',
      'Καταχώριση αιτημάτων βλάβης και άμεση παρακολούθηση επίλυσης',
      'Κράτηση κοινόχρηστων χώρων, δήλωση οχημάτων και θέσεων στάθμευσης',
    ],
    joinLabel: 'Σύνδεση στην πύλη',
    joinNote: 'Οι ένοικοι συνδέονται στην πύλη κατόπιν πρόσκλησης μέσω email από τη διαχείριση ή τον ιδιοκτήτη.',
    dualIdentity: {
      title: 'Ενιαίος λογαριασμός για διαχειριστές και συνιδιοκτήτες',
      body: 'Μέλη της διαχειριστικής επιτροπής που είναι ταυτόχρονα ιδιοκτήτες ή ένοικοι εναλλάσσονται άμεσα μεταξύ διαχείρισης και προσωπικού προφίλ από την ίδια οθόνη.',
    },
  },
  trust: {
    eyebrow: 'Ασφαλεια', title: 'Διαβαθμισμένη πρόσβαση και ασφάλεια δεδομένων',
    facts: [
      { title: 'Πρόσβαση βάσει ρόλων', body: 'Διαχειριστές, ελεγκτές και ένοικοι έχουν πρόσβαση αποκλειστικά στα δεδομένα που αντιστοιχούν στον ρόλο τους.' },
      { title: 'Passkeys και ταυτοποίηση δύο παραγόντων', body: 'Προστασία λογαριασμών με σύγχρονα passkeys, εφαρμογές 2FA και ασφαλείς κωδικούς ανάκτησης.' },
      { title: 'Αυστηρός διαχωρισμός χώρων εργασίας', body: 'Όλα τα στοιχεία ακινήτων και ενοίκων είναι αυστηρά απομονωμένα στον χώρο εργασίας στον οποίο ανήκουν.' },
      { title: 'Κρυπτογραφημένα αντίγραφα και ιστορικό ενεργειών', body: 'Δυνατότητα εξαγωγής κρυπτογραφημένων αντιγράφων ασφαλείας και πλήρες αρχείο καταγραφής ενεργειών (audit log).' },
    ],
  },
  pricing: {
    eyebrow: 'Τιμολογηση', title: 'Διαφανή προγράμματα για κάθε μέγεθος ακινήτου',
    body: 'Απλές, ξεκάθαρες τιμές χωρίς κρυφά κόστη. Όλα τα προγράμματα περιλαμβάνουν πλήρη δωρεάν δοκιμή 30 ημερών με πλήρη ενημέρωση πριν από οποιαδήποτε χρέωση.',
    chooser: 'Επιλέξτε το πρόγραμμα που ταιριάζει στον τρόπο διαχείρισής σας',
    basicChoice: 'Για αυτοδιαχειριζόμενες πολυκατοικίες',
    proChoice: 'Για επαγγελματίες και εταιρείες διαχείρισης',
    basic: {
      name: 'Basic', audience: 'Αυτοδιαχειριζόμενη πολυκατοικία', propertyScope: 'Ένα ακίνητο, έως 20 διαμερίσματα',
      features: ['Υπολογισμός κοινοχρήστων και έκδοση ειδοποιητηρίων', 'Κατανομή ισομερώς, βάσει εμβαδού, υπνοδωματίων ή χιλιοστών', 'Συγκεντρωτικές καταστάσεις, υπόλοιπα και εκτυπώσιμες αποδείξεις', 'Αιτήματα συντήρησης, εξωτερικοί συνεργάτες και αρχείο εγγράφων', 'Πύλη ενοίκων με ανακοινώσεις, ψηφοφορίες και κρατήσεις χώρων', 'Μητρώο οχημάτων και θέσεων στάθμευσης', 'Συμφωνία τραπεζικών κινήσεων και πάγιες χρεώσεις', 'Διεπαφή σε Ελληνικά και Αγγλικά'],
      cta: 'Δημιουργία λογαριασμού',
    },
    pro: {
      name: 'Portfolio Pro', audience: 'Εταιρεία διαχείρισης', propertyScope: 'Απεριόριστα ακίνητα',
      minimum: 'Ελάχιστο €50 / μήνα',
      features: ['Όλες οι λειτουργίες του Basic σε απεριόριστα ακίνητα', 'Άμεση εναλλαγή μεταξύ ακινήτων χαρτοφυλακίου', 'Πρόσβαση προσωπικού ανά ρόλο σε κάθε ακίνητο', 'Οικονομική και λειτουργική εικόνα σε όλο το χαρτοφυλάκιο', 'Υποστήριξη προτεραιότητας για μεγάλα χαρτοφυλάκια'],
      cta: 'Δημιουργία λογαριασμού',
    },
    footnote: 'Όλα τα προγράμματα ξεκινούν με δωρεάν δοκιμή. Τα στοιχεία συνδρομής επιβεβαιώνονται πριν από οποιαδήποτε ενεργοποίηση.',
  },
  faq: {
    eyebrow: 'Ερωτησεις', title: 'Συχνές ερωτήσεις',
    items: [
      { question: 'Πώς κατανέμονται τα κοινόχρηστα;', answer: 'Επιλέγετε τη μέθοδο επιμερισμού για κάθε δαπάνη: ισομερώς, βάσει εμβαδού (m²), αριθμού υπνοδωματίων ή των χιλιοστών της πολυκατοικίας. Το Koinon υπολογίζει το ακριβές ποσό συμμετοχής ανά διαμέρισμα με ακρίβεια λεπτού, ώστε το άθροισμα των επιμέρους χρεώσεων να ταυτίζεται πάντα με τη συνολική δαπάνη.' },
      { question: 'Τι συμβαίνει αν κάποιος πληρώσει παραπάνω;', answer: 'Το επιπλέον ποσό παραμένει ως πιστωτικό υπόλοιπο στην καρτέλα του διαμερίσματος. Ένα πιστωτικό δεν συμψηφίζεται ποτέ αυθαίρετα με οφειλές άλλου διαμερίσματος, εξασφαλίζοντας απόλυτη λογιστική διαφάνεια.' },
      { question: 'Πόσο διαρκεί η μηνιαία έκδοση κοινοχρήστων;', answer: 'Οι πάγιες δαπάνες καταχωρούνται μία φορά και επαναλαμβάνονται αυτόματα. Το Koinon υπολογίζει ακαριαία την κατανομή, τα ειδοποιητήρια και τα υπόλοιπα, μετατρέποντας τη μηνιαία έκδοση σε έναν απλό έλεγχο λίγων λεπτών.' },
      { question: 'Ποιοι μπορούν να χρησιμοποιήσουν το Koinon;', answer: 'Αυτοδιαχειριζόμενες πολυκατοικίες, επαγγελματικές εταιρείες διαχείρισης και ένοικοι. Ένας χρήστης μπορεί να έχει διαχειριστικό ρόλο και ταυτόχρονα να είναι συνδεδεμένος ως ένοικος στο διαμέρισμά του, εναλλάσσοντας προβολή με ένα κλικ.' },
      { question: 'Έχουν οι κάτοικοι δική τους ηλεκτρονική πρόσβαση;', answer: 'Ναι. Ο κάτοικος συνδέεται κατόπιν πρόσκλησης μέσω email και έχει άμεση πρόσβαση στο υπόλοιπό του, στα αναλυτικά ειδοποιητήρια, στις αποδείξεις, στα αιτήματα βλάβης, στις ανακοινώσεις, καθώς και στα οικονομικά της πολυκατοικίας και του ταμείου.' },
      { question: 'Υποστηρίζεται εισαγωγή τραπεζικών κινήσεων;', answer: 'Ναι. Εισάγετε το ηλεκτρονικό αρχείο κινήσεων (extrait) σε μορφή CSV ή OFX. Το Koinon προτείνει αυτόματη αντιστοίχιση εισπράξεων με οφειλές και καταχωρεί μόνο ό,τι εγκρίνετε. Αν εισαγάγετε ξανά το ίδιο αρχείο, αναγνωρίζει τις ήδη καταχωρισμένες κινήσεις ώστε να αποκλείονται οι διπλοεγγραφές.' },
      { question: 'Μπορώ να απενεργοποιήσω λειτουργίες που δεν χρειάζομαι;', answer: 'Ναι. Η διαχείριση βλαβών, η τραπεζική συμφωνία, τα έγγραφα, οι κοινόχρηστοι χώροι, οι ψηφοφορίες και τα οχήματα ενεργοποιούνται ή απενεργοποιούνται ανά ακίνητο, ώστε να εμφανίζονται μόνο όσα χρησιμοποιείτε.' },
      { question: 'Τι συμβαίνει μετά τη λήξη της δωρεάν δοκιμής;', answer: 'Απαιτείται ενεργή συνδρομή για τη συνέχιση των καταχωρίσεων. Το επιλεγμένο πρόγραμμα και οι λεπτομέρειες χρέωσης παρουσιάζονται αναλυτικά πριν από οποιαδήποτε ενεργοποίηση.' },
    ],
  },
  demo: {
    eyebrow: 'Ζωντανή επίδειξη',
    title: 'Δείτε ένα πραγματικό χαρτοφυλάκιο πριν εγγραφείτε',
    body: 'Περιηγηθείτε {{workspaces}} πλήρως συμπληρωμένους χώρους εργασίας που καλύπτουν {{properties}} ακίνητα και {{units}} διαμερίσματα, με ζωντανά οικονομικά αρχεία, αιτήματα βλάβης, ανακοινώσεις, ψηφοφορίες και έγγραφα.',
    readOnly: 'Μόνο ανάγνωση',
    readOnlyNote: 'Η επίδειξη είναι μόνο για ανάγνωση. Μπορείτε να δείτε τα πάντα, αλλά καμία ενέργειά σας δεν αλλάζει τα δεδομένα.',
    email: 'Email',
    password: 'Κωδικός',
    cta: 'Σύνδεση στην επίδειξη',
    footnote: 'Δεν απαιτείται εγγραφή. Ο λογαριασμός καλύπτει όλους τους χώρους εργασίας που αναφέρονται παραπάνω.',
  },
  finalCta: {
    eyebrow: 'Ξεκινηστε σημερα',
    title: 'Οργανώστε την πολυκατοικία σας με το Koinon',
    body: 'Δημιουργήστε τον χώρο εργασίας σας, καταχωρίστε το κτίριο και τα διαμερίσματα και ολοκληρώστε τον πρώτο σας μηνιαίο κύκλο με δωρεάν δοκιμή 30 ημερών.',
    managerCta: 'Δημιουργία λογαριασμού',
    managerNote: 'Για διαχειριστικές επιτροπές και εταιρείες διαχείρισης.',
    residentCta: 'Σύνδεση στην πύλη ενοίκων',
  },
  footer: { tagline: 'Σύγχρονη διαχείριση ακινήτων και κοινότητας για πολυκατοικίες, διαχειριστές και ενοίκους.', product: 'Προϊόν', account: 'Λογαριασμός', copyright: '© 2026 Koinon. Όλα τα δικαιώματα διατηρούνται.' },
};

export const LANDING_CONTENT: Record<'en' | 'el', LandingContent> = { en, el };

export function getLandingContent(language?: string): LandingContent {
  const selected = language?.startsWith('el') ? el : en;
  if (selected === en) return en;
  return {
    ...en,
    ...selected,
    footer: { ...en.footer, ...selected.footer },
    meta: { ...en.meta, ...selected.meta },
    nav: { ...en.nav, ...selected.nav },
    ui: { ...en.ui, ...selected.ui },
  };
}